// ReplayControls.tsx - Timeline scrubber with play/pause and speed controls
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause, X } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import type { ReplaySpeed } from "../hooks/useReplayState";
import "./ReplayControls.css";

type ReplayControlsProps = {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  speed: ReplaySpeed;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (time: number) => void;
  onSpeedChange: (speed: ReplaySpeed) => void;
  onExitReplay: () => void;
};

// Format seconds to MM:SS
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

const SPEEDS: ReplaySpeed[] = [0.5, 1, 2, 4];

export function ReplayControls({
  currentTime,
  duration,
  isPlaying,
  speed,
  onPlay,
  onPause,
  onSeek,
  onSpeedChange,
  onExitReplay,
}: ReplayControlsProps) {
  const { t } = useTranslation();

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSeek(parseFloat(e.target.value));
    },
    [onSeek]
  );

  const handleSpeedClick = useCallback(() => {
    const currentIndex = SPEEDS.indexOf(speed);
    const nextIndex = (currentIndex + 1) % SPEEDS.length;
    onSpeedChange(SPEEDS[nextIndex]);
  }, [speed, onSpeedChange]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="replay-controls">
      {/* Play/Pause button */}
      <Button
        variant="primary"
        iconOnly
        size="sm"
        onClick={isPlaying ? onPause : onPlay}
        title={isPlaying ? t("common.stop") : t("common.run")}
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
      </Button>

      {/* Time display */}
      <span className="replay-time">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>

      {/* Timeline slider */}
      <div className="replay-slider-container">
        <input
          type="range"
          className="replay-slider"
          min={0}
          max={duration}
          step={0.1}
          value={currentTime}
          onChange={handleSliderChange}
          style={
            {
              "--progress": `${progress}%`,
            } as React.CSSProperties
          }
          aria-label="Replay timeline"
        />
      </div>

      {/* Speed button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleSpeedClick}
        title={t("session.replaySpeed")}
        aria-label={`Speed: ${speed}x`}
      >
        {speed}x
      </Button>

      {/* Exit button */}
      <Button
        variant="ghost"
        iconOnly
        size="sm"
        onClick={onExitReplay}
        title={t("session.exitReplay")}
        aria-label="Exit replay"
      >
        <X size={14} />
      </Button>
    </div>
  );
}

export default ReplayControls;
